import { Component, ReactNode } from 'react';

import { RootState } from '@iwProvider/configureStore';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './ChooseDocumentType.module.css';
import { DocumentTypeLabel } from './DocumentTypeLabel';
import { AgedDebtorReportSVG } from '../../../../svgComponents/agedDebtorReportSVG';
import { BankStatementSVG } from '../../../../svgComponents/bankStatementSVG';
import { BusinessPlanSVG } from '../../../../svgComponents/businessPlanSVG';
import { FiledAccountsSVG } from '../../../../svgComponents/filedAccountsSVG';
import { ManagementAccountsSVG } from '../../../../svgComponents/managementAccountsSVG';
import { VATReturnSVG } from '../../../../svgComponents/vatReturnSVG';
import { DOCUMENT_UPLOADER } from '../documentUploader/DocumentUploader.actions';
import { DocumentTypeAction } from './documentTypeAction/DocumentTypeAction';
import { getApplicantFirstName } from '../../../../api/lendingAPI/CustomerStateAPIHelper';
import { TDocumentTypes } from '../UploadDocumentsModalHelper';

const mapStateToProps = ({ api }: RootState) => ({
    firstName: getApplicantFirstName(api.customer_state.data)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToDocumentUploader: (documentType: TDocumentTypes[number]) =>
        dispatch({ type: DOCUMENT_UPLOADER, documentType })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

class ChooseDocumentType extends Component<
    PropsFromRedux & { documentTypes: TDocumentTypes }
> {
    handleDocumentTypeSelection = (documentType: TDocumentTypes[number]) => {
        this.props.goToDocumentUploader(documentType);
    };

    // NOTE: this returns SVG component *classes*, not elements. `DocumentTypeAction`
    // renders the result as a child (`{icon}`), and React renders a class reference
    // (rather than `<Icon />`) to nothing — so these icons have never actually appeared.
    // This is a pre-existing latent bug, preserved here to keep behaviour unchanged under
    // the React 18 upgrade (the result is cast to ReactNode at the call site below to
    // satisfy the stricter @types/react@18, which no longer accepts a component class as
    // a ReactNode). Follow-up: render `<Icon />` to make the icons appear (needs design sign-off).
    handleIconSelection = (document: { value: string }) => {
        switch (document.value) {
            case 'bank statement':
                return BankStatementSVG;
            case 'aged debtor report':
                return AgedDebtorReportSVG;
            case 'invoices':
                return FiledAccountsSVG;
            case 'business plan':
                return BusinessPlanSVG;
            case 'management accounts':
            case 'trade certificate':
                return ManagementAccountsSVG;
            case 'vat return':
            case 'personal id':
            case 'shareholder id':
            case 'share registry':
            case 'other':
            default:
                return VATReturnSVG;
        }
    };

    renderDocuments() {
        const { documentTypes } = this.props;

        return _.map(
            documentTypes,
            (document: TDocumentTypes[number], documentKey: number) => {
                if (document && !document.hideFromSelection) {
                    return (
                        <DocumentTypeAction
                            key={documentKey}
                            documentType={document.value}
                            onClick={() =>
                                this.handleDocumentTypeSelection(document)
                            }
                            label={<DocumentTypeLabel label={document.label} />}
                            icon={
                                this.handleIconSelection(
                                    document
                                ) as unknown as ReactNode
                            }
                        />
                    );
                }
            }
        );
    }

    render() {
        const documents = this.renderDocuments();

        return (
            <div>
                <div className={styles.title}>
                    <FormattedMessage
                        id="_.trading_data.choose_document_type.title"
                        defaultMessage="What document type would you like to upload?"
                    />
                </div>
                <div className={styles.actions}>{documents}</div>
            </div>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(ChooseDocumentType);
